import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, FileText, Inbox, FileCode2,
  ClipboardList, Settings, LogOut, Menu,
  PenLine, Mail, Plug, Users,
} from 'lucide-react';
import { can } from '../lib/permissions';

const NAV = [
  { to: '/dashboard',        icon: LayoutDashboard, label: 'Dashboard',        permission: null },
  { to: '/documents',        icon: FileText,         label: 'Documents',        permission: 'documents.read' },
  { to: '/envelopes',        icon: Inbox,            label: 'Envelopes',        permission: 'envelopes.read' },
  { to: '/templates',        icon: FileCode2,        label: 'Templates',        permission: 'templates.read' },
  { to: '/email-templates',  icon: Mail,             label: 'Email Templates',  permission: 'templates.read' },
  { to: '/integrations',     icon: Plug,             label: 'Integrations',     permission: 'integrations.write' },
  { to: '/audit-log',        icon: ClipboardList,    label: 'Audit Log',        permission: 'audit.read' },
  { to: '/admin/users',      icon: Users,            label: 'Team',             permission: 'users.manage' },
  { to: '/settings',         icon: Settings,         label: 'Settings',         permission: 'settings.write' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 py-5 border-b border-slate-700/60">
        <div className="w-8 h-8 bg-blue-600 rounded-sm flex items-center justify-center">
          <PenLine size={16} className="text-white" />
        </div>
        <span className="font-semibold text-white text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
          SignatureFlow
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {NAV.filter(({ permission }) => !permission || can(user, permission)).map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'
              }`
            }
            data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
          >
            <Icon size={17} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="px-3 py-4 border-t border-slate-700/60">
        <div className="flex items-center gap-3 px-3 py-2 mb-1">
          <div className="w-8 h-8 rounded-sm bg-blue-500/30 flex items-center justify-center">
            <span className="text-blue-300 text-sm font-semibold">{(user?.name || 'U')[0].toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            <p className="text-[10px] text-blue-300 uppercase tracking-wide mt-0.5">{user?.role || 'member'}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-slate-300 hover:bg-red-600/20 hover:text-red-300 transition-colors"
          data-testid="logout-button"
        >
          <LogOut size={17} />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 bg-slate-900 flex-col flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-slate-900">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 gap-3 flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-1.5 rounded text-slate-500 hover:bg-slate-100"
            data-testid="mobile-menu-btn"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-1 text-xs text-slate-500 lg:hidden">
            <PenLine size={14} className="text-blue-600" />
            <span className="font-semibold text-slate-700" style={{ fontFamily: 'Outfit' }}>SignatureFlow</span>
          </div>
          <div className="flex-1" />
          <span className="hidden sm:block text-sm text-slate-600">
            Welcome, <span className="font-medium text-slate-900">{user?.name}</span>
          </span>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
